import { createSelector } from 'reselect'

import {
  areOrderStayMappingsReady,
  buildOrderReviewProgress,
  getOrderStayMappingLookups,
  getResolvedOrderStayMappings,
  hasOrderStayMappingFailure,
  type OrderReviewProgress,
} from 'lib/order/reviewableStays'

const EMPTY_REVIEW_DELETION = { loading: false, error: false }

export function getReviewDeletion(state: App.State, reviewId?: string) {
  return (reviewId ? state.review.reviewDeletion?.[reviewId] : undefined) ?? EMPTY_REVIEW_DELETION
}

export const isOrderReviewed = createSelector(
  (state: App.State, orderId: string) => state.review.reviewsByOrder[orderId],
  (state: App.State, orderId: string) => !!state.review.reviewsByOrderLoading[orderId],
  (reviewsByOrder, isLoading) => {
    return !isLoading && !!reviewsByOrder?.length
  },
)

export const isOrderReviewsLoaded = createSelector(
  (state: App.State, orderId: string) => state.review.reviewsByOrder[orderId],
  (state: App.State, orderId: string) => !!state.review.reviewsByOrderLoading[orderId],
  (state: App.State, orderId: string) => !!state.review.reviewsByOrderError?.[orderId],
  (reviewsByOrder, isLoading, hasError) => {
    return !isLoading && !hasError && !!reviewsByOrder
  },
)

export const getOrderStayPropertyMappings = createSelector(
  (_state: App.State, order: App.Order) => order,
  (state: App.State) => state.offer.propertyOfferMappings,
  (order, propertyOfferMappings) => {
    const lookups = getOrderStayMappingLookups(order)
    return getResolvedOrderStayMappings(lookups, propertyOfferMappings)
  },
)

export const getOrderStayReviewProgress = createSelector(
  (_state: App.State, order: App.Order) => order,
  (state: App.State, order: App.Order) => state.review.reviewsByOrder[order.id],
  (state: App.State, order: App.Order) => !!state.review.reviewsByOrderLoading[order.id],
  (state: App.State, order: App.Order) => !!state.review.reviewsByOrderError?.[order.id],
  (state: App.State) => state.offer.propertyOfferMappings,
  (order, reviews, loading, error, propertyOfferMappings): OrderReviewProgress => {
    const lookups = getOrderStayMappingLookups(order)
    const mappingsReady = areOrderStayMappingsReady(lookups, propertyOfferMappings)
    return buildOrderReviewProgress(
      order,
      { reviews, loading, error },
      getResolvedOrderStayMappings(lookups, propertyOfferMappings),
      mappingsReady,
      hasOrderStayMappingFailure(lookups, propertyOfferMappings),
    )
  },
)
